'use client';

import { ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  RoundedModal,
  type RoundedModalContainerWidth,
  type RoundedModalContainerHeight,
} from '../rounded-modal/rounded-modal';

export type ImperativeModalOptions = {
  width?: RoundedModalContainerWidth;
  height?: RoundedModalContainerHeight;
  showCloseButton?: boolean;
  className?: string;
  classNames?: { overlay?: string };
};

type ImperativeModalContextType = {
  openModal: (content: ReactNode, options?: ImperativeModalOptions) => void;
  closeModal: () => void;
};

const ImperativeModalContext = createContext<ImperativeModalContextType | undefined>(undefined);

/**
 * Provider for imperative modal management.
 * Wraps your app to enable opening modals programmatically from anywhere.
 *
 * @example
 * ```tsx
 * <ImperativeModalProvider>
 *   <App />
 * </ImperativeModalProvider>
 * ```
 */
export const ImperativeModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalContent = useRef<ReactNode | null>(null);
  const modalOptions = useRef<ImperativeModalOptions>({});

  const openModal = useCallback((content: ReactNode, options?: ImperativeModalOptions) => {
    modalContent.current = content;
    modalOptions.current = options || {};
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    modalContent.current = null;
    modalOptions.current = {};
  }, []);

  return (
    <ImperativeModalContext.Provider
      value={{
        openModal,
        closeModal,
      }}>
      {children}
      <RoundedModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        width={modalOptions.current.width || 'responsive-sm'}
        height={modalOptions.current.height || 'auto-height'}
        className={modalOptions.current.className}
        classNames={modalOptions.current.classNames}>
        {modalContent.current}
      </RoundedModal>
    </ImperativeModalContext.Provider>
  );
};

export const useImperativeModal = () => {
  const context = useContext(ImperativeModalContext);
  if (!context) {
    throw new Error('useImperativeModal must be used within ImperativeModalProvider');
  }
  return context;
};
